import { EventEmitter, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { LoginRequest } from 'src/app/core/models/request/login.request';
import { IdentityService } from 'src/app/core/services/identity.service';
import { User } from '../models/user';
import { RestService } from './rest.service';
import { StorageService } from './storage.service';
import { ILoginResponse, INewLoginResponse } from '../models/response/login.response';
import { loginRequest } from '../models/request/Ga.request';
import { gaInterface } from 'src/app/pages/public/go-token-register/ga-token-register.interface';
import { NotificationService } from './notification.service';
import { StorageConstants } from '../constants/storage.constants';
import { NativeBridgeService } from './native-bridge.service';
import { deviceMessageId } from '../enums/device-message-id.enum';
import { GeneralConstants } from '../constants/general.constants';
import { LoginCloverRequest } from '../models/request/login-clover.request';
import { CloverService } from './clover.service';
import { getNavigatorInfo } from '../utils/navigator.service';

@Injectable()
export class LoginService {
  private readonly TERMS_CONDITIONS_LINK: string = 'https://www.yacare.com/terminos-y-condiciones/';
  private readonly MAX_LOGIN_ATTEMPTS: number = 10;
  private readonly LOCKED_LOGIN_MS: number = 5 * 60 * 1000;
  private readonly LOGIN_ATTEMPT_INTERVAL_MSECONDS: number = 3000;
  private failedLoginAttempts: number;
  private lastFailedLoginAttemptDate: Date;
  loadingFinish = new EventEmitter();

  constructor(
    private storage: StorageService,
    private rest: RestService,
    private router: Router,
    private identityService: IdentityService,
    private bridgeService: NativeBridgeService,
    private notificationService: NotificationService,
    private cloverService: CloverService
  ) {}

  public saveSession(user: ILoginResponse, saveToLocalStorage: boolean = true): void {
    this.identityService.saveSession(user, saveToLocalStorage);
  }

  async endSession(): Promise<void> {
    this.clearSessionStorage();
    if (
      this.bridgeService.nativeBridgeIsActive('androidBridge') ||
      this.bridgeService.nativeBridgeIsActive('iOSBridge')
    ) {
      this.bridgeService.sendMessageToDevice(JSON.stringify({ message_id: deviceMessageId.USER_SIGN_OUT }));
      console.log('Sent userSignOut to bridge');
    } else {
      console.warn('Bridge no disponible, solo navega a login');
    }
    this.router.navigate(['/login']);
  }

  clearSessionStorage(useSessionStorage: boolean = false): void {
    localStorage.removeItem('FR-SDK-APM0009908_YACARE');
    this.identityService.clearSession(useSessionStorage);
  }

  public getCurrentLogged(): User | undefined {
    return this.identityService.getUser();
  }

  public async authenticate(flowId: string, cookie: string): Promise<ILoginResponse> {
    let userlogin = new LoginRequest();
    userlogin.flow_id = flowId;
    userlogin.cookie = cookie;
    userlogin.appVersion = this.identityService.getYacareVersion();
    userlogin.uuid =
      this.identityService.getUUID() !== 'undefined' && this.identityService.getUUID()
        ? this.identityService.getUUID()
        : null;

    try {
      let response = await this.rest.post('security/pf-ws/authn/issue-token', userlogin);
      response.uuid !== null ? this.identityService.setUUID(response.uuid) : null;

      return response.object;
    } catch (error) {
      if (error.message !== 'null') {
        throw new Error(error.message);
      }
      throw new Error('Por favor vuelva a intentar en 5 minutos');
    }
  }

  /**
   *
   * @param login user email
   * @param password user password
   */
  public async preLoginClover(login: string, password: string): Promise<object> {
    let userlogin = new LoginCloverRequest();
    userlogin.username = login;
    userlogin.password = password;
    userlogin.appVersion = this.identityService.getYacareVersion();
    userlogin.terminal_id = this.cloverService.getTerminalId();
    userlogin.version = getNavigatorInfo().version;

    try {
      return await this.rest.postLoginClover('security/clover/authenticate', userlogin);
    } catch (error) {
      throw this.notificationService.showError(error.message);
    }
  }

  /**
   *
   * @param login email del usuario
   * @param password contraseña del usuario
   * @param uc si contiene un codigo de 2FA en el equipo actual
   */
  public async preAuthorizeLogin(login: string, password: string): Promise<INewLoginResponse> {
    let userlogin = new loginRequest();
    userlogin.username = login;
    userlogin.password = password;
    try {
      let response = await this.rest.post('security/pf-ws/authn/initiate-flow', userlogin);
      return response.object;
    } catch (error) {
      throw this.notificationService.showError(error.message || 'Ha ocurrido un error');
    }
  }

  public async getinfoGaCreate(data): Promise<gaInterface> {
    try {
      let response = await this.rest.post(`security/pf-ws/authn/pairing-device`, data);
      return response.object;
    } catch (error) {
      throw this.notificationService.showError(error.message);
    }
  }

  public addLoginAttempts() {
    this.getLoginAttemptParameters();
    if (
      new Date().getTime() - new Date(this.lastFailedLoginAttemptDate).getTime() <
      this.LOGIN_ATTEMPT_INTERVAL_MSECONDS
    ) {
      //Si la diferencia entre intentos es de menos de 3 segundos, aumentar el contador de intentos fallidos.
      this.storage.setObject(StorageConstants.failedLoginAttempts, ++this.failedLoginAttempts);
      this.storage.setObject(StorageConstants.lastFailedLoginAttemptDate, new Date());
    } else {
      //Si es de mas de 3 segundos entonces resetear el contador.
      this.storage.setObject(StorageConstants.failedLoginAttempts, 0);
      this.storage.setObject(StorageConstants.lastFailedLoginAttemptDate, new Date());
    }
  }

  public isLoginlocked(): boolean {
    this.getLoginAttemptParameters();
    //si hay mas de MAX_LOGIN_ATTEMPTS intentos fallidos y no han transcurrido LOCKED_LOGIN_MINUTES: está bloqueado
    if (
      +this.failedLoginAttempts >= this.MAX_LOGIN_ATTEMPTS &&
      new Date().getTime() - this.lastFailedLoginAttemptDate.getTime() < this.LOCKED_LOGIN_MS
    ) {
      return true;
    } else {
      return false;
    }
  }

  private getLoginAttemptParameters() {
    this.failedLoginAttempts = this.storage.getAsObject(StorageConstants.failedLoginAttempts);
    this.lastFailedLoginAttemptDate = new Date(this.storage.getAsObject(StorageConstants.lastFailedLoginAttemptDate));
    if (this.failedLoginAttempts == null || this.lastFailedLoginAttemptDate == null) {
      this.failedLoginAttempts = 0;
      this.lastFailedLoginAttemptDate = new Date();
      this.storage.setObject(StorageConstants.failedLoginAttempts, this.failedLoginAttempts);
      this.storage.setObject(StorageConstants.lastFailedLoginAttemptDate, this.lastFailedLoginAttemptDate);
    }
  }

  /**
   * Actualiza la contraseña del usuario usando el código recibido por email
   */
  updatePassword(data: { codigo: string; password: string; email: string; platform: number }) {
    return this.rest.post(GeneralConstants.updatePassword, data);
  }

  /**
   * Remueve los dispositivos asociados al usuario usando el código recibido por email
   */
  removeDevice(data: { codigo: string; email: string; password: string; platform: number }) {
    return this.rest.post(GeneralConstants.removeDevice, data);
  }
}
